import { ColliAction, ColliState } from '@frontend/colli/types';
import { EventLevel, EventType } from '@frontend/events/types';
import { CommonMessage } from '@frontend/lang';
import { PackageAction, PackageState } from '@frontend/package/types';
import { TransactionAction, TransactionState } from '@frontend/transaction/types';
import { ReactNode } from 'react';

const HISTORY_LOG_PATTERN = /^(transaction|colli|package)_log_(.+)$/;

type HistoryLogEntity = 'transaction' | 'colli' | 'package';

const ERROR_HISTORY_LOG_SUFFIXES = new Set(['slot_not_opened', 'slot_not_closed']);

const HISTORY_LOG_LABELS: Record<HistoryLogEntity, Record<string, ReactNode>> = {
    transaction: {
        completed: CommonMessage.OBJECTS.TRANSACTION.HISTORY_LOG.COMPLETED,
        created: CommonMessage.OBJECTS.TRANSACTION.HISTORY_LOG.CREATED,
        dropped_off: CommonMessage.OBJECTS.TRANSACTION.HISTORY_LOG.DROPPED_OFF,
        picked_up: CommonMessage.OBJECTS.TRANSACTION.HISTORY_LOG.PICKED_UP
    },
    colli: {
        completed: CommonMessage.OBJECTS.COLLI.HISTORY_LOG.COMPLETED,
        created: CommonMessage.OBJECTS.COLLI.HISTORY_LOG.CREATED,
        dropped_off: CommonMessage.OBJECTS.COLLI.HISTORY_LOG.DROPPED_OFF,
        picked_up: CommonMessage.OBJECTS.COLLI.HISTORY_LOG.PICKED_UP
    },
    package: {
        completed: CommonMessage.OBJECTS.PACKAGE.HISTORY_LOG.COMPLETED,
        created: CommonMessage.OBJECTS.PACKAGE.HISTORY_LOG.CREATED,
        dropped_off: CommonMessage.OBJECTS.PACKAGE.HISTORY_LOG.DROPPED_OFF,
        picked_up: CommonMessage.OBJECTS.PACKAGE.HISTORY_LOG.PICKED_UP,
        slot_not_opened: CommonMessage.OBJECTS.PACKAGE.HISTORY_LOG.SLOT_NOT_OPENED,
        slot_not_closed: CommonMessage.OBJECTS.PACKAGE.HISTORY_LOG.SLOT_NOT_CLOSED
    }
};

/**
 * Returns a human-readable event type label.
 * History-logged types are shown as "* event logged" instead of "* history logged".
 */
export function getEventTypeLabel(type: string): ReactNode {
    switch (type) {
        case EventType.TRANSACTION_HISTORY_LOGGED:
            return CommonMessage.EVENTS.TRANSACTION_EVENT_LOGGED;
        case EventType.COLLI_HISTORY_LOGGED:
            return CommonMessage.EVENTS.COLLI_EVENT_LOGGED;
        case EventType.USER_HISTORY_LOGGED:
            return CommonMessage.EVENTS.USER_EVENT_LOGGED;
        default:
            return type.replace(/_/g, ' ');
    }
}

/**
 * True when history-log event data represents an error (e.g. SLOT not opened/closed).
 */
export function isErrorHistoryLogData(data: string | null): boolean {
    if (!data) {
        return false;
    }

    const historyLogMatch = data.match(HISTORY_LOG_PATTERN);
    if (!historyLogMatch) {
        return false;
    }

    return ERROR_HISTORY_LOG_SUFFIXES.has(historyLogMatch[2]);
}

/**
 * True when the event should be rendered as an error in the history timeline.
 */
export function isErrorHistoryEvent(event: { level: EventLevel; data: string | null }): boolean {
    return event.level === EventLevel.ERROR || isErrorHistoryLogData(event.data);
}

/** Title className for history timeline entries (applies error styling when needed). */
export function getHistoryEventTitleClassName(event: { level: EventLevel; data: string | null }): string {
    return `text-base font-semibold antialiased${isErrorHistoryEvent(event) ? ' text-status-danger' : ''}`;
}

/**
 * Maps event.data to a translated label.
 * Handles history-log values (e.g. transaction_log_picked_up), states, and actions.
 */
export function mapEventDataToDisplay(data: string | null): ReactNode {
    if (!data) {
        return '';
    }

    const historyLogMatch = data.match(HISTORY_LOG_PATTERN);
    if (historyLogMatch) {
        const entity = historyLogMatch[1] as HistoryLogEntity;
        const suffix = historyLogMatch[2];
        return HISTORY_LOG_LABELS[entity]?.[suffix] ?? `${entity} ${suffix}`.replace(/_/g, ' ');
    }

    const state =
        TransactionState.ALL.find((item) => item.value === data) ??
        ColliState.ALL.find((item) => item.value === data) ??
        PackageState.ALL.find((item) => item.value === data);
    if (state) {
        return state.displayName;
    }

    const action =
        TransactionAction.ALL.find((item) => item.value === data) ??
        ColliAction.ALL.find((item) => item.value === data) ??
        PackageAction.ALL.find((item) => item.value === data);
    if (action) {
        return action.displayName;
    }

    return data.replace(/_/g, ' ');
}
